import React, {useContext, useEffect, useState} from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Select, {SelectOption} from "@amzn/meridian/select";
import escapeRegExp from "lodash/escapeRegExp"
import Button from "@amzn/meridian/button";
import {chainWalk, getRefreshInterval} from "../util/Util";
import Table, {TableActionBar, TableCell, TableRow} from "@amzn/meridian/table";
import {FormattedMessage, injectIntl} from "react-intl";
import {ForceContext} from "./context/ForceContext";
import {FORCE_INDUCT_ALERTS, FORCE_INDUCT_FIELDS, FORCE_STATUS} from "./Constants";
import Alert from "@amzn/meridian/alert";
import {CycleManagementContext} from "./context/CycleManagementContext";
import {ConfigContext} from "../global/context/ConfigContext";
import {ACTION_TYPE, FEATURES} from "../util/Constants";
import {getFeaturesFromParent} from "../handler/ParentDataHandler";

const ForceInduct = ({intl}) => {
    const {forceViewModel, forceDispatch} = useContext(ForceContext)
    const {cycleViewModel} = useContext(CycleManagementContext)
    const {configViewModel, configDispatch} = useContext(ConfigContext);
    const [searchQuery, setSearchQuery] = useState()

    const searchRegExp = new RegExp(escapeRegExp(searchQuery), "i")
    const matchedOptions = Object.keys(forceViewModel.stationCycleConfig).filter(
        cycleName => !searchQuery || searchRegExp.test(cycleName)
    )

    const getForceData = () => {
        forceDispatch({type: 'LOAD_VIEW', data: {dispatcher: forceDispatch}})
    }

    const getFeatureStatus = () => {
        getFeaturesFromParent([FEATURES.IS_AUTO_FORCE_INDUCT_ENABLED]).then(featureStatus => {
            configDispatch({type: ACTION_TYPE.GET_FEATURES_STATUS, featureStatus: featureStatus.data});
        });
    }

    const enableForceCycle = () => {
        if (forceViewModel.selectedCycleName !== "") {
            forceDispatch({type: 'ENABLE_FORCE_CYCLE', data: {dispatcher: forceDispatch}})
        }
    }

    const automateForceCycle = () => {
        if (forceViewModel.selectedCycleName !== "") {
            forceDispatch({type: 'AUTOMATE_FORCE_CYCLE', data: {dispatcher: forceDispatch}})
        }
    }

    const disableForceCycle = (cycleName) => {
        forceDispatch({type: 'DISABLE_FORCE_CYCLE', data: {cycleName: cycleName, dispatcher: forceDispatch}})
    }

    const activateForceCycle = () => {
        switch (forceViewModel.selectedForceInductStatus) {
            case FORCE_STATUS.ENABLED:
                enableForceCycle();
                break;
            case FORCE_STATUS.AUTOMATED:
                automateForceCycle();
                break;
            case FORCE_STATUS.DISABLED:
                disableForceCycle(forceViewModel.selectedCycleName);
                break;
            default:
                break;
        }
    }

    const getAlert = (alert) => {
        return (alert.id !== "" ?
            <Alert size="medium" type={alert.type}
                   onClose={() => forceDispatch({type: "CLEAR_ALERT"})}>
                <FormattedMessage id={alert.id} defaultMessage={alert.default} values={alert.values}/>
            </Alert> : null)
    }

    const getStaticAlert = (alert) => {
        return (alert.id !== "" ?
            <Alert size="medium" type={alert.type}>
                <FormattedMessage id={alert.id} defaultMessage={alert.default} values={alert.values}/>
            </Alert> : null)
    }

    useEffect(() => {
        if (cycleViewModel.isNodeEnabled) {
            getForceData();
            getFeatureStatus();
        }
        const interval = setInterval(() => {
            if (cycleViewModel.isNodeEnabled) {
                getForceData();
            }
        }, getRefreshInterval());
        return () => clearInterval(interval);
    }, []);

    const isNodeEnabledForAutoForceInduct = chainWalk(() =>
        configViewModel.featureStatus[FEATURES.IS_AUTO_FORCE_INDUCT_ENABLED], false);

    const getTableHeader = () => {
        return(
            <TableRow header={true}>
                <TableCell>
                    <FormattedMessage id="inductmanagement.cycle" defaultMessage="Cycle"/>
                </TableCell>
                <TableCell>
                    <FormattedMessage id="inductmanagement.force_induct" defaultMessage="Force induct"/>
                </TableCell>
                <TableCell>
                    <FormattedMessage id="inductmanagement.last_updated_by" defaultMessage="Last updated by"/>
                </TableCell>
                { isNodeEnabledForAutoForceInduct ?
                    null
                    :
                    <TableCell>
                        <FormattedMessage id="inductmanagement.disableForceInduct"
                                          defaultMessage="Disable force induct"/>
                    </TableCell>
                }
            </TableRow>
        )
    }

    const getTableActionBar = () => {
        return (
                <Row widths="fill" overflowX="auto">
                    <TableActionBar widths={[200, 200, "fill", 200]}>
                    <Select
                        value={forceViewModel.selectedCycleName}
                        onChange={(data) => forceDispatch({type: "SELECT_CYCLE", data: {cycleName: data}})}
                        searchQuery={searchQuery}
                        onSearch={setSearchQuery}
                        placeholder={intl.formatMessage({id:"inductmanagement.enterCycle", default: "Enter Cycle..."})}
                        width={200}>
                        {matchedOptions.map(key => (
                            <SelectOption
                                label={forceViewModel.stationCycleConfig[key]}
                                value={forceViewModel.stationCycleConfig[key]}
                                key={key}
                            />
                        ))}
                        {!matchedOptions.length ? (
                            <Column alignmentVertical="center" spacing="100" spacingInset="100">
                                <FormattedMessage id={"inductmanagement.noCycles"} default={"No cycles available"}/>
                            </Column>
                        ) : null}
                    </Select>

                    { isNodeEnabledForAutoForceInduct ?
                        <Select
                            disabled={forceViewModel.selectedCycleName === ""}
                            value={forceViewModel.selectedForceInductStatus}
                            onChange={(data) => {forceDispatch({type: "SELECT_FORCE_INDUCT_STATUS", data: {forceInductStatus: data}})}}
                            placeholder={intl.formatMessage({
                                id: "inductmanagement.selectStatus",
                                default: "Select Status..."
                            })}
                            width={200}>

                        { Object.keys(FORCE_STATUS).map(key => (
                            <SelectOption
                                label={key}
                                value={FORCE_STATUS[key]}
                                key={key}
                            />
                        )) }
                    </Select> : <div/>
                }

                    <div/>
                { isNodeEnabledForAutoForceInduct ?
                    <Button type="primary"
                            disabled={forceViewModel.selectedCycleName === "" || forceViewModel.selectedForceInductStatus === ""}
                            onClick={activateForceCycle}>
                        <FormattedMessage id={"inductmanagement.submit"} defaultMessage={"Submit"}/>
                        </Button>
                    :
                    <Button type="primary"
                        onClick={enableForceCycle}>
                            <FormattedMessage id={"inductmanagement.enableForceInduct"} defaultMessage={"Enable force induct"}/>
                        </Button>
                    }
                    </TableActionBar>
                </Row>
        )
    }

    const getTableRows = () => {
        return (
            Object.keys(forceViewModel.forceCycleConfig).map((index) => (
                <TableRow key={index}>
                    <TableCell header={true} key={"cycleName"}> {forceViewModel.forceCycleConfig[index].cycleName} </TableCell>
                    {FORCE_INDUCT_FIELDS.map((field) => {
                        return (<TableCell key={field}>{forceViewModel.forceCycleConfig[index][field]}</TableCell>);
                    })}
                    { isNodeEnabledForAutoForceInduct ?
                        null
                        :
                        <TableCell key={index} width="grid-2">
                            <Button onClick={() => disableForceCycle(forceViewModel.forceCycleConfig[index].cycleName)}>
                                <FormattedMessage id={"inductmanagement.disableForceInduct"}
                                                  defaultMessage={"Disable force induct"}/>
                            </Button>
                        </TableCell>
                    }
                </TableRow>
            ))
        )
    }

    if (cycleViewModel.isNodeEnabled === false) {
        return (getStaticAlert(FORCE_INDUCT_ALERTS.NOT_ENABLED))
    }
    return (
        <Column spacing="300">
            {getAlert(forceViewModel.alert)}
            {getTableActionBar()}
            <Table headerRows={1} spacing="small" showStripes={true} widths={[200, 200, "fill"]}>
                {getTableHeader()}
                {Object.keys(forceViewModel.forceCycleConfig).length > 0 ? getTableRows() : null}
            </Table>
        </Column>
    )
}

export default injectIntl(ForceInduct)


// WEBPACK FOOTER //
// ./src/components/inductmanagementv2/ForceInduct.js